import { Close } from "@carbon/icons-react";
import { Typography } from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import PageLoader from "../../components/PageLoader";
import AsyncButton from "../../components/buttons/AsyncButton";
import CancelButton from "../../components/buttons/backButton/CancelButton";
import FallbackComponent from "../../components/fallbackComponent/FallbackComponent";
import OrderHeader from "../../components/orderHeader/OrderHeader";
import { useOrderContext } from "../../contexts/OrderContext";
import { useHeader } from "../../contexts/PageHeaderContext";
import { SavedProgressReducerActionTypes } from "../../contexts/reducers/savedProgressReducer";
import useConfirmInboundDeliveryQuantitiesMutation from "../../hooks/graphql-hooks/mutations/useConfirmInboundDeliveryQuantitiesMutation";
import useInboundDeliveryByIdQuery from "../../hooks/graphql-hooks/queries/useInboundDeliveryByIdQuery";
import useNavigateTo from "../../hooks/useNavigateTo";
import {
  InboundDeliveryFragment,
  InboundDeliveryStatus,
} from "../../models/autogenerated-deliveryService-gql/graphql";
import { IDeliveryItem } from "../../models/deliveryItem";
import {
  mapToConfirmInboundDeliveryQuantitiesInput,
  mapToIDeliveryItems,
} from "../../utils/mapper";
import EditableDeliveryItems from "../newOrder/components/itemLists/deliveryItems/EditableDeliveryItems";
import { emptyGeneralInfoFormData } from "../newOrder/screens/generalInfoScreen/GeneralInfoScreen";

interface ConfirmQuantitiesProps {}

const ConfirmQuantities = (props: ConfirmQuantitiesProps) => {
  const { t } = useTranslation();
  const { id: idFromUrl } = useParams();
  const { navigateBack } = useNavigateTo();
  const { updateHeader, updateSecondHeader } = useHeader();
  const { dispatchSavedProgress } = useOrderContext();

  const [deliveryItems, setDeliveryItems] = useState<IDeliveryItem[]>([]);

  const { loading, error } = useInboundDeliveryByIdQuery({
    id: idFromUrl,
    dataAction: (inboundDelivery) => {
      setDeliveryItems(mapToIDeliveryItems(inboundDelivery.items));
      initializeLocationId(inboundDelivery);
      updateHeaders(inboundDelivery);
    },
    errorAction: () => {
      updateHeader(<>{t("delivery")}</>);
    },
  });

  const [confirmMutation, { loading: loadingMutation }] =
    useConfirmInboundDeliveryQuantitiesMutation({
      dataAction: (output) => {
        enqueueSnackbar(t("confirmQuantitiesSuccess"), {
          variant: "success",
        });
        navigateBack();
      },
    });

  //the AddDialog needs the locationId in the savedProgress to query the owners
  //we need ths initialization to be able to be able to re use the component
  const initializeLocationId = (inboundDelivery: InboundDeliveryFragment) => {
    dispatchSavedProgress({
      type: SavedProgressReducerActionTypes.SET_PROP,
      property: "generalInfo",
      value: {
        ...emptyGeneralInfoFormData,
        locationId: inboundDelivery.locationResolved?.id ?? "",
      },
    });
  };

  const sendConfirmMutation = () => {
    if (!idFromUrl || deliveryItems.length === 0) return;

    const confirmQuantitiesInput = mapToConfirmInboundDeliveryQuantitiesInput(
      idFromUrl,
      deliveryItems,
    );

    if (!confirmQuantitiesInput) return;

    confirmMutation({ variables: { confirmQuantitiesInput } }).catch(() => {});
  };

  const updateHeaders = (inboundDelivery: InboundDeliveryFragment) => {
    const { deliveryNumber } = inboundDelivery;

    updateHeader(<>{t("confirmQuantities")}</>);
    updateSecondHeader(<> {`${t("deliveryNumber")}: ${deliveryNumber}`}</>);
  };

  const renderContent = () => {
    if (loading) return <PageLoader />;

    if (error)
      return (
        <FallbackComponent
          middleIcon={<Close size={32} />}
          text={`${t("queryError")}. ${t("tryAgainLater")}`}
        />
      );

    return (
      <>
        <Typography align="center" marginBottom="24px" variant="h6">
          {t("confirmQuantitiesDescription")}
        </Typography>
        <EditableDeliveryItems
          deliveryItems={deliveryItems}
          setDeliveryItems={setDeliveryItems}
        />
      </>
    );
  };

  return (
    <div className="page-content">
      <div>
        <Helmet title={t("confirmQuantities")} />
        <OrderHeader
          paddingBottom="16px"
          amount={deliveryItems.length}
          //@ts-ignore
          status={InboundDeliveryStatus.Open}
        />
        {renderContent()}
      </div>
      <div>
        <div className="page-content-button-container-button-full-width">
          <AsyncButton
            sendRequest={sendConfirmMutation}
            loading={loadingMutation}
            disabled={deliveryItems.length === 0 || loadingMutation}
            variant="contained"
          >
            {t("confirmQuantities")}
          </AsyncButton>
        </div>
        <div className="page-content-button-container">
          <div className="page-content-button-container-button-center">
            <CancelButton />
          </div>
        </div>
      </div>
    </div>
  );
};
export default ConfirmQuantities;
